export const CHECKOUT_URL = 'https://functionalcoachsystem.impultienda.ar/checkout'
export const PRICING_ANCHOR = '#pricing'

export const IMAGES = {
  productBundle:
    'https://cdn.magicpatterns.com/uploads/mEKpLUzgcAiVLhmSguTYTk/ChatGPT_Image_5_sept_2026,_11_32_04.png',
  coachKettlebell:
    'https://cdn.magicpatterns.com/patterns/generated-images/206c865e-f556-4ea4-9bfe-ab124f59c3a8.jpg',
  coachPlanning:
    'https://cdn.magicpatterns.com/patterns/generated-images/6d725ecb-e24b-47eb-b4c4-9e8a4682c02e.jpg',
}

export const TRUST_LINE = 'Immediate Digital Access • One-Time Payment • 7-Day Guarantee'

export type Problem = {
  number: string
  title: string
  body: string
  icon: 'calendar' | 'shuffle' | 'search' | 'timer'
}

export const PROBLEMS: Problem[] = [
  {
    number: '01',
    title: 'Sunday Night Programming',
    icon: 'calendar',
    body: 'You spend hours every week planning sessions from zero. By Monday morning, you have already used valuable time and energy before coaching your first client.',
  },
  {
    number: '02',
    title: 'Sessions Without Structure',
    icon: 'shuffle',
    body: 'You know plenty of exercises, but turning them into cohesive sessions with clear progression is another challenge.',
  },
  {
    number: '03',
    title: 'Information Overload',
    icon: 'search',
    body: 'Exercises, methods, videos and programming ideas are everywhere — but organizing them into one repeatable coaching framework is difficult.',
  },
  {
    number: '04',
    title: 'The Rep Counter Trap',
    icon: 'timer',
    body: 'You became a coach to help people progress, but too much of your time disappears into planning, logistics and figuring out what comes next.',
  },
]

export const WORKFLOW = ['Assess', 'Select', 'Build', 'Coach', 'Track', 'Progress']

export const SYSTEM_PILLARS = [
  'Exercise Selection',
  'Session Design',
  'Programming',
  'Progression',
  'Professional Coaching',
  'Client Organization',
]

export type Module = {
  number: string
  title: string
  body: string
  icon: 'foundations' | 'library' | 'builder' | 'sessions' | 'progression' | 'coaching'
}

export const MODULES: Module[] = [
  {
    number: '01',
    title: 'Functional Training Foundations',
    icon: 'foundations',
    body: 'Master the principles behind smarter movement, training and programming decisions.',
  },
  {
    number: '02',
    title: '250+ Exercise Library',
    icon: 'library',
    body: 'Exercises, coaching cues, progressions, regressions and substitutions in one organized reference.',
  },
  {
    number: '03',
    title: 'Workout Builder System',
    icon: 'builder',
    body: 'Build structured 30, 45 and 60-minute sessions without starting from a blank page.',
  },
  {
    number: '04',
    title: '50 Ready-To-Use Sessions',
    icon: 'sessions',
    body: 'Complete functional sessions across different goals, levels and training environments.',
  },
  {
    number: '05',
    title: 'Client Progression System',
    icon: 'progression',
    body: 'Know when to progress, hold or regress clients using a repeatable decision framework.',
  },
  {
    number: '06',
    title: 'Professional Coaching & Client Success System',
    icon: 'coaching',
    body: 'Improve cueing, communication, session delivery, organization and client management.',
  },
]

export type Bonus = {
  number: string
  title: string
  body: string
}

export const BONUSES: Bonus[] = [
  {
    number: '1',
    title: 'Functional Session Scriptbook™',
    body: 'Ready-to-use coaching language, cues, corrections and communication frameworks.',
  },
  {
    number: '2',
    title: '12-Week Functional Challenge Calendar™',
    body: 'A structured 12-week framework for client engagement, organization and consistency.',
  },
  {
    number: '3',
    title: 'Functional Assessment Quickstart Kit™',
    body: 'Connect intake, movement observation and goal setting to smarter session planning.',
  },
  {
    number: '4',
    title: 'Functional Equipment Circuit Playbook™',
    body: 'Ready-to-use circuit structures for dumbbells, kettlebells, bands, suspension trainers, medicine balls and bodyweight.',
  },
  {
    number: '5',
    title: 'Client Progress Dashboard Pack™',
    body: 'Organize client progress, milestones and review conversations more clearly.',
  },
]

export type Benefit = {
  title: string
  body: string
  icon: 'stress' | 'confidence' | 'progression' | 'time'
}

export const BENEFITS: Benefit[] = [
  {
    title: 'Less Programming Stress',
    icon: 'stress',
    body: 'Start with a professional framework instead of a blank page.',
  },
  {
    title: 'More Coaching Confidence',
    icon: 'confidence',
    body: 'Walk into sessions knowing the objective, structure, exercise options and coaching priorities.',
  },
  {
    title: 'Clearer Client Progression',
    icon: 'progression',
    body: 'Use organized progression principles instead of delivering disconnected workouts week after week.',
  },
  {
    title: 'More Time For Actual Coaching',
    icon: 'time',
    body: 'Spend less time rebuilding sessions and more time coaching the people in front of you.',
  },
]

export const OFFER_INCLUDES = [
  'FUNCTIONAL COACH SYSTEM™',
  '6 Core Professional Modules',
  '250+ Exercise Library',
  '50 Ready-to-Use Sessions',
  'Workout Builder System',
  'Client Progression System',
  'Professional Coaching Resources',
  '5 Professional Bonuses',
  'Immediate Digital Access',
  'One-Time Payment',
]

export type Testimonial = {
  name: string
  role: string
  quote: string
}

export const TESTIMONIALS: Testimonial[] = [
  {
    name: 'James R.',
    role: 'Personal Trainer & Studio Owner — London, UK',
    quote:
      'Programming used to take up far too much of my week. Having the exercises, session structures and progression framework organized in one system makes planning much faster and my sessions feel far more intentional.',
  },
  {
    name: 'Sarah M.',
    role: 'Functional Coach — Sydney, Australia',
    quote:
      'The Workout Builder is probably my favorite part. Instead of staring at a blank page, I have a clear framework I can adapt to the client in front of me. It makes the whole coaching process feel more organized.',
  },
  {
    name: 'Marcus T.',
    role: 'Strength & Conditioning Coach — Toronto, Canada',
    quote:
      'What I like most is that this isn’t just a collection of random workouts. The exercise library, programming framework and progression system actually connect, so you can build sessions with a clear purpose.',
  },
  {
    name: 'Rachel K.',
    role: 'Functional Training Studio Owner — Miami, USA',
    quote:
      'When you’re coaching multiple clients and classes every week, constantly rebuilding everything becomes exhausting. Having one organized system for exercises, sessions and progression makes the workflow much easier.',
  },
  {
    name: 'David L.',
    role: 'Personal Trainer — Manchester, UK',
    quote:
      'The exercise library gives me a much clearer way to adapt sessions when equipment, ability or client needs change. I can find an alternative quickly without losing the purpose of the session.',
  },
]

export const FAQS = [
  {
    q: 'Is this a physical or digital product?',
    a: 'Functional Coach System™ is a digital professional resource. Access is delivered electronically after purchase.',
  },
  {
    q: 'Do I need advanced coaching experience?',
    a: 'No. It works for newer professionals building structure and for experienced coaches who want a faster, more organized workflow. Users remain responsible for working within their professional qualifications and scope.',
  },
  {
    q: 'Does it work for one-on-one and group sessions?',
    a: 'Yes. The system includes frameworks and examples applicable to individual coaching, small groups and functional training classes.',
  },
  {
    q: 'How quickly can I start using the system?',
    a: 'You can begin exploring and applying the frameworks as soon as you receive digital access.',
  },
  {
    q: 'Do I receive the bonuses immediately?',
    a: 'Yes. The five professional bonuses are included and delivered together with the main system.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. It is a single one-time payment — not a subscription.',
  },
  {
    q: 'Can I print the materials?',
    a: 'The included PDF resources can be viewed digitally and may be printed for your own professional use where appropriate.',
  },
  {
    q: 'What equipment is required?',
    a: 'The system covers multiple training environments, including bodyweight, dumbbell, kettlebell, resistance-band, suspension-training, medicine-ball and limited-equipment options.',
  },
  {
    q: 'Can I use it for online coaching?',
    a: 'Many of the programming, exercise-selection and progression frameworks can also support remote coaching workflows when appropriately adapted.',
  },
  {
    q: 'How does the 7-day guarantee work?',
    a: 'You have 7 full days to review the system. If it is not right for you, you can request a refund within the guarantee period according to the refund policy.',
  },
]

export const PURCHASE_NOTIFICATIONS = [
  { name: 'Michael', location: 'Austin, USA' },
  { name: 'Sophie', location: 'London, UK' },
  { name: 'Daniel', location: 'Toronto, Canada' },
  { name: 'Emma', location: 'Sydney, Australia' },
  { name: 'James', location: 'Manchester, UK' },
  { name: 'Olivia', location: 'Melbourne, Australia' },
]
